import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { BookOpen, Loader2, Trash2, Upload } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { runAiJob } from '../../lib/aiJob';
import { AddAuthorForm, AddWorkForm } from './DoctrineForms';
import { DoctrineVsRulings } from './DoctrineVsRulings';

const CHUNK = 4 * 1024 * 1024;

const UploadBtn = ({ work, onDone }) => {
  const { api } = useAuth();
  const ref = useRef(null);
  const [pct, setPct] = useState(null);
  const upload = async (file) => {
    if (!file) return;
    const id = crypto.randomUUID();
    const total = Math.ceil(file.size / CHUNK);
    try {
      for (let i = 0; i < total; i += 1) {
        await api.post(`/doctrine/upload/${id}/chunk`, file.slice(i * CHUNK, (i + 1) * CHUNK), { params: { index: i }, headers: { 'Content-Type': 'application/octet-stream' } });
        setPct(Math.round(((i + 1) / total) * 90));
      }
      const r = await runAiJob(api, `/doctrine/upload/${id}/finish`, { work_id: work.id, filename: file.name, total }, { timeout: 600000 });
      setPct(100);
      toast.success(`«${work.title}»: ${r.pages} página${r.pages === 1 ? '' : 's'} leída${r.pages === 1 ? '' : 's'}, ${r.chunks} fragmento${r.chunks === 1 ? '' : 's'} para Fidem`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || 'No se pudo subir'); } finally { setPct(null); if (ref.current) ref.current.value = ''; }
  };
  return (
    <>
      <input ref={ref} type="file" accept=".pdf,.docx,.txt" className="hidden" data-testid={`doctrine-file-${work.key}`} onChange={(e) => upload(e.target.files[0])} />
      <button data-testid={`doctrine-upload-${work.key}`} disabled={pct !== null} onClick={() => ref.current?.click()} className="flex items-center gap-1 rounded-full border border-slate-600 px-2.5 py-1 text-[11px] text-slate-300 hover:border-amber-500 disabled:opacity-60">
        {pct !== null ? <><Loader2 className="h-3 w-3 animate-spin" />{pct}%</> : <><Upload className="h-3 w-3" />{work.my_chunks ? 'Reemplazar mi ejemplar' : 'Subir mi ejemplar'}</>}
      </button>
    </>
  );
};

// Doctrina de Ignacio Vidal Domínguez: catalog, public texts and the user's own (private) copies used by CRM, Cerebro, Fidem and briefs
export const DoctrinePanel = () => {
  const { api } = useAuth();
  const [d, setD] = useState(null);
  const [authors, setAuthors] = useState([]);
  const [author, setAuthor] = useState('Ignacio Vidal Domínguez');
  const loadAuthors = () => api.get('/doctrine/authors').then((r) => setAuthors(r.data.authors)).catch(() => {});
  const load = () => api.get('/doctrine/works', { params: { author } }).then((r) => { setD(r.data); if (r.data.syncing) setTimeout(load, 15000); }).catch(() => setD({ works: [] }));
  useEffect(() => { load(); }, [author]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadAuthors(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const removeMine = async (w) => {
    try { await api.delete(`/doctrine/works/${w.id}/mine`); toast.success('Tu ejemplar fue eliminado'); load(); } catch { toast.error('No se pudo eliminar'); }
  };
  if (!d) return null;
  const books = d.works.filter((w) => w.kind !== 'artículo');
  const articles = d.works.filter((w) => w.kind === 'artículo');
  const Row = ({ w }) => (
    <div data-testid={`doctrine-work-${w.key}`} className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-800 bg-slate-900/40 px-3 py-2">
      <div className="min-w-0 flex-1">
        <p className="text-sm text-white">«{w.title}»{w.year ? ` (${w.year})` : ''}</p>
        <p className="text-[11px] text-slate-500">{w.kind} · {w.publisher}{w.topics ? ` · ${w.topics}` : ''}</p>
      </div>
      <span className={`rounded-full px-2 py-0.5 text-[10px] ${w.chunks ? 'bg-emerald-500/15 text-emerald-300' : 'bg-slate-800 text-slate-400'}`}>
        {w.chunks ? `${w.public_text ? 'texto público' : 'tu ejemplar'} · ${w.chunks} fragmentos` : 'solo ficha'}
      </span>
      {!w.public_text && <UploadBtn work={w} onDone={load} />}
      {w.my_chunks > 0 && <button data-testid={`doctrine-delete-${w.key}`} onClick={() => removeMine(w)} aria-label="Eliminar mi ejemplar" className="p-1 text-slate-500 hover:text-rose-300"><Trash2 className="h-3.5 w-3.5" /></button>}
    </div>
  );
  return (
    <section data-testid="doctrine-panel" className="mb-10 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-6">
      <h2 className="mb-1 flex items-center gap-2 text-lg font-bold"><BookOpen className="h-5 w-5 text-amber-400" />Doctrina notarial y registral</h2>
      <p className="mb-3 max-w-3xl text-xs text-slate-400">CRM, Cerebro, Fidem y los escritos citan esta doctrina con obra y página, en un apartado separado de la jurisprudencia. Los libros tienen derechos de autor: sube tus ejemplares (PDF, DOCX o TXT) y quedan privados en tu cuenta.</p>
      <div data-testid="doctrine-authors" className="mb-3 flex flex-wrap gap-1.5">
        {authors.map((a) => (
          <button key={a.id} data-testid={`doctrine-author-${a.key.replace(/\s+/g, '-')}`} onClick={() => setAuthor(a.name)} className={`rounded-full border px-3 py-1 text-xs transition-colors ${author === a.name ? 'border-amber-500 bg-amber-500 text-slate-950' : 'border-slate-600 text-slate-300 hover:border-amber-500'}`}>{a.name} · {a.works}</button>
        ))}
      </div>
      <AddAuthorForm onAdded={(n) => { loadAuthors(); setAuthor(n); setTimeout(() => { loadAuthors(); load(); }, 20000); }} />
      <AddWorkForm author={author} onAdded={() => { load(); loadAuthors(); }} />
      <div className="space-y-2">{books.map((w) => <Row key={w.id} w={w} />)}</div>
      {articles.length > 0 && (<><p className="mb-2 mt-5 text-xs font-semibold text-slate-300">Artículos en revistas ({articles.length})</p><div className="space-y-2">{articles.map((w) => <Row key={w.id} w={w} />)}</div></>)}
      {!d.works.length && <p data-testid="doctrine-no-works" className="text-xs text-slate-500">Aún no hay obras de este autor. Fidem busca sus artículos publicados; también puedes agregar sus libros a mano.</p>}
      <DoctrineVsRulings author={author} />
    </section>
  );
};
